import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { getProfileCompletionStatus } from "../../../services/childcare/profile";

const STAFF_ROLES = ["childcare_co_admin", "childcare_caregiver", "childcare_tutor"];
const CACHE_KEY = "childcare_profile_complete";

/**
 * Childcare-specific route guard.
 *
 * - Not authenticated  → redirect to /childcare/login
 * - Wrong user type    → redirect to /access-denied
 * - Staff with incomplete profile → redirect to /childcare/complete-profile
 *
 * The completion check is cached in localStorage to avoid repeated API calls.
 * Clear the cache key on logout and when profile is saved.
 */
const ChildcareProtectedRoute = ({ children, allowedUserTypes = [] }) => {
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isStaff = STAFF_ROLES.includes(user.user_type);

  // Read cached completion result. null = not yet fetched.
  const cached = localStorage.getItem(CACHE_KEY);
  const [profileComplete, setProfileComplete] = useState(
    cached !== null ? cached === "true" : null,
  );

  useEffect(() => {
    // Skip if: not authenticated, not a staff role, or already cached.
    if (!token || !isStaff || cached !== null) return;

    getProfileCompletionStatus()
      .then((res) => {
        const complete = res.data?.data?.isComplete ?? true;
        localStorage.setItem(CACHE_KEY, String(complete));
        setProfileComplete(complete);
      })
      .catch(() => {
        // Fail-open: don't block access when the API is unreachable.
        setProfileComplete(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auth check ────────────────────────────────────────────────────────────
  if (!token) {
    return <Navigate to="/childcare/login" replace />;
  }

  if (allowedUserTypes.length > 0 && !allowedUserTypes.includes(user.user_type)) {
    return <Navigate to="/access-denied" replace />;
  }

  // ── Completion check ──────────────────────────────────────────────────────
  // Show a minimal loader while the async check is in progress.
  if (isStaff && profileComplete === null) {
    return (
      <div style={{
        display: "flex", justifyContent: "center", alignItems: "center",
        height: "100vh", flexDirection: "column", gap: 12,
      }}>
        <div style={{
          width: 32, height: 32, border: "3px solid #efa12f",
          borderTopColor: "transparent", borderRadius: "50%",
          animation: "spin 0.7s linear infinite",
        }} />
      </div>
    );
  }

  // Redirect incomplete staff profiles — but never on the complete-profile page itself.
  const pathname = window.location.pathname;
  if (isStaff && profileComplete === false && pathname !== "/childcare/complete-profile") {
    return <Navigate to="/childcare/complete-profile" replace />;
  }

  return children;
};

export default ChildcareProtectedRoute;
